import { useTranslate } from 'react-admin'
import { isToday } from 'date-fns'
import isTimePast from '../utils/helpers/dateHelpers/isTimePast'
import isTimeDifferenceAboveCertainDuration from '../utils/helpers/dateHelpers/isTimeDifferenceAboveCertainDuration'
import ORCHESTRATOR_LIVESTREAM_MINIMUM_DURATION from '../utils/constants/orchestratorLivestreamMinimumDuration'
import isDateBefore from '../utils/helpers/dateHelpers/isDateBefore'
import isObjectEmptyHelper from '../utils/helpers/isObjectEmptyHelper'
import { STREAM_FEATURES, supports } from '../utils/constants/streamTypeCapabilities'
import GAME_EVENT_FORM_BROADCAST_FIELDS from '../utils/constants/gameEventFormBroadcastFields'

// `savedEvent` is the game being edited, as last saved (absent when creating one).
const useEventFormsValidations = (formName, broadcastId, hasSetYoutube, useOrgYoutube, savedEvent) => {
  const translate = useTranslate()

  // Whether the stats settings differ from the saved game: Track Stats switched, or the statisticians changed.
  const statsSettingsChanged = (values) => {
    const savedIds = savedEvent?.statisticians?.map((s) => s.id) ?? []
    const ids = values?.statisticianIds ?? []
    return (
      Boolean(values?.hasStats) !== Boolean(savedEvent?.hasStats) ||
      ids.length !== savedIds.length ||
      ids.some((id) => !savedIds.includes(id))
    )
  }

  // A game tracking stats needs a statistician, but only when it is created or its stats settings
  // change: a game saved with stats on and nobody assigned (every CMS-synced game starts that way)
  // can still be edited for anything else, such as adding a Video operator.
  const applyStatisticianRequired = (values, errors) => {
    if (!values?.hasStats || values?.statisticianIds?.length) return
    if (savedEvent && !statsSettingsChanged(values)) return
    errors.statisticianIds = translate('ra.validationErrors.requiredField')
  }

  // Games require the visible matchup + schedule fields for every game (club and federation,
  // regardless of streaming), so submit highlights the actual on-screen inputs.
  // homeTeam/awayTeam are objects carrying a `name` (federation team or club free-text opponent).
  // The court is NOT in here: the API only needs one when the game has a stream
  // (isRecord || isBroadcast), so stats-only orgs can create games without a court.
  const applyGameRequired = (values, errors) => {
    const req = translate('ra.validationErrors.requiredField')
    if (!values?.fromDate) errors.fromDate = req
    if (!values?.startTime) errors.startTime = req
    if (!values?.endTime) errors.endTime = req
    if (!values?.homeTeam?.name) errors.homeTeam = req
    if (!values?.awayTeam?.name) errors.awayTeam = req
  }

  // A game that records or broadcasts runs on a court's camera, so the court is required
  // exactly when the game has a stream — mirroring the API's own condition.
  const applyGameCourtRequired = (values, errors) => {
    if (formName !== 'game') return
    if (!(values?.isBroadcast || values?.isRecord)) return
    if (!values?.courtId) errors.courtId = translate('ra.validationErrors.courtId')
  }

  // A YouTube-destined broadcast needs a title and a visibility: the API rejects either being
  // missing (EventService.ValidateYtConfigFields), and an omitted visibility used to bind
  // silently to Private. Validate here so the error lands on the field instead of arriving as a
  // 400 — the form submits with noValidate, so the fields' `required` prop only draws an
  // asterisk. Mirrors the API's own condition: a broadcast that carries a YouTube config.
  const applyYtConfigRequired = (values, errors) => {
    const ytConfig = values?.[GAME_EVENT_FORM_BROADCAST_FIELDS.EVENT_YT_CONFIG]
    if (!values?.[GAME_EVENT_FORM_BROADCAST_FIELDS.IS_BROADCAST]) return
    if (isObjectEmptyHelper(ytConfig)) return

    const req = translate('ra.validationErrors.requiredField')
    const ytErrors = {}
    if (!ytConfig[GAME_EVENT_FORM_BROADCAST_FIELDS.BROADCAST_TITLE]?.trim()) {
      ytErrors[GAME_EVENT_FORM_BROADCAST_FIELDS.BROADCAST_TITLE] = req
    }
    if (!ytConfig[GAME_EVENT_FORM_BROADCAST_FIELDS.YOUTUBE_VISIBILITY]) {
      ytErrors[GAME_EVENT_FORM_BROADCAST_FIELDS.YOUTUBE_VISIBILITY] = req
    }

    if (Object.keys(ytErrors).length > 0) {
      errors[GAME_EVENT_FORM_BROADCAST_FIELDS.EVENT_YT_CONFIG] = ytErrors
    }
  }

  // TODO Add mapper or fix structure.
  const createLivestreamEventValidations = (values) => {
    const {
      isBroadcast,
      isRecord,
      fromDate,
      startTime,
      endTime,
      place,
      courtId,
      teams,
      type,
      streamDestination,
      homeTeam,
      awayTeam,
    } = values || {}

    const hasStream = isBroadcast || isRecord

    const errorsCreate = {}

    if (formName === 'game') {
      applyGameRequired(values, errorsCreate)
    } else if (!teams?.some(Boolean)) {
      errorsCreate.teams = translate('ra.validationErrors.requiredField')
    }

    applyStatisticianRequired(values, errorsCreate)

    if (hasStream) {
      applyGameCourtRequired(values, errorsCreate)
      // Compare full datetimes: a game starting at 22:00 may end at 00:00 the next day
      // (the end time field rolls past-midnight ends over to the next day).
      if (
        !isTimeDifferenceAboveCertainDuration(
          startTime,
          endTime,
          ORCHESTRATOR_LIVESTREAM_MINIMUM_DURATION,
        )
      ) {
        errorsCreate.endTime = translate('ra.validationErrors.endTime')
      }
    }

    if (isBroadcast) {
      if (isDateBefore(new Date(fromDate))) {
        errorsCreate.fromDate = translate('ra.validationErrors.fromDate')
      }

      if (isToday(new Date(fromDate)) && isTimePast(startTime)) {
        errorsCreate.startTime = translate('ra.validationErrors.startTime')
      }

      if (formName === 'practice' && place === 'Court' && !courtId) {
        errorsCreate.courtId = translate('ra.validationErrors.courtId')
      }
      if (!hasSetYoutube && !useOrgYoutube && (!streamDestination || streamDestination.length < 1)) {
        errorsCreate.streamDestination = translate('ra.validationErrors.requiredField')
      }
      if (supports(type, STREAM_FEATURES.COURT_LOGOS) && !homeTeam?.name) {
        errorsCreate.homeTeam = translate('ra.validationErrors.requiredField')
      }
      if (supports(type, STREAM_FEATURES.COURT_LOGOS) && !awayTeam?.name) {
        errorsCreate.awayTeam = translate('ra.validationErrors.requiredField')
      }
      applyYtConfigRequired(values, errorsCreate)
    }

    return errorsCreate
  }

  const editLivestreamEventValidations = (values) => {
    const {
      isBroadcast,
      isRecord,
      startTime,
      endTime,
      teams,
      type,
      streamDestination,
      awayTeam,
      homeTeam,
    } = values || {}

    const hasStream = isBroadcast || isRecord

    let errorsEdit = {}

    if (formName === 'game') {
      applyGameRequired(values, errorsEdit)
    } else if (!teams?.some(Boolean)) {
      errorsEdit.teams = translate('ra.validationErrors.requiredField')
    }

    applyStatisticianRequired(values, errorsEdit)

    if (hasStream) {
      if (!broadcastId) {
        errorsEdit = createLivestreamEventValidations(values)
      } else {
        applyGameCourtRequired(values, errorsEdit)
        if (
          !isTimeDifferenceAboveCertainDuration(
            startTime,
            endTime,
            ORCHESTRATOR_LIVESTREAM_MINIMUM_DURATION,
          )
        ) {
          errorsEdit.endTime = translate('ra.validationErrors.endTime')
        }
      }
    }

    if (isBroadcast) {
      if (!hasSetYoutube && !useOrgYoutube && (!streamDestination || streamDestination.length < 1)) {
        errorsEdit.streamDestination = translate('ra.validationErrors.requiredField')
      }
      if (supports(type, STREAM_FEATURES.COURT_LOGOS) && !homeTeam?.name) {
        errorsEdit.homeTeam = translate('ra.validationErrors.requiredField')
      }
      if (supports(type, STREAM_FEATURES.COURT_LOGOS) && !awayTeam?.name) {
        errorsEdit.awayTeam = translate('ra.validationErrors.requiredField')
      }
      applyYtConfigRequired(values, errorsEdit)
    }

    return errorsEdit
  }

  return { createLivestreamEventValidations, editLivestreamEventValidations }
}

export default useEventFormsValidations
